import { BoardDownloadsPage } from '@/ui/features/board-files';
import { DialogHelper, showAlertDialog } from '@juvare/common-web-ui/DialogHelper';
import { GlobalApp, GlobalAppRouteObject } from '@juvare/common-web-ui/GlobalApp';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { NavigationContext } from '@juvare/common-web-ui/NavigationContext';
import { SnackbarHelper } from '@juvare/common-web-ui/SnackbarHelper';
import { formatDateCustom } from '@juvare/common-web-ui/utils';
import { EditableSettingsAdminPage } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import { useUserClient } from '@juvare/platform-sdk-react/ui/hooks';
import { getAppManifest, getUserState, platformSlice } from '@juvare/platform-sdk-react/ui/stores';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import HomeIcon from '@mui/icons-material/Home';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import PeopleIcon from '@mui/icons-material/People';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { Avatar, Typography } from '@mui/material';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Outlet } from 'react-router-dom';
import { AppContext } from './Context';
import { HomePage } from './features/home';
import { UserManagementPage } from './features/user-management';
import { useTranslation } from './hooks';

export const UserApp: React.FC = () => {
    const { t } = useTranslation();
    const appManifest = useSelector(getAppManifest);
    const appContext = React.useContext(AppContext);
    const { email, firstName, lastName, permissions } = useSelector(getUserState);
    const navigationContext = React.useContext(NavigationContext);

    const dispatch = useDispatch();
    const userClient = useUserClient();
    const logOutOnClick = () =>
        navigationContext.navigate(() => {
            userClient.logout().finally(() => {
                appContext.initState();
                dispatch(platformSlice.actions.clearUser());
            });
        });

    const profileName = firstName || lastName ? firstName + ' ' + lastName : email;
    const initials = firstName || lastName ? firstName.charAt(0) + lastName.charAt(0) : email.charAt(0);

    const routes: GlobalAppRouteObject[] = [];
    if (permissions.length === 1 && permissions[0] === 'download') {
        routes.push({
            index: true,
            element: <BoardDownloadsPage />,
            handle: {
                type: 'page',
                label: t('home'),
                icon: <HomeOutlinedIcon />,
                iconSelected: <HomeIcon />,
                navigateTo: '/.',
            },
        });
        routes.push({
            path: 'downloads',
            element: <Navigate to='/' />,
        });
    } else {
        routes.push({
            index: true,
            element: <HomePage />,
            handle: {
                type: 'page',
                label: t('home'),
                icon: <HomeOutlinedIcon />,
                iconSelected: <HomeIcon />,
                navigateTo: '/.',
            },
        });
        if (permissions.includes('download')) {
            routes.push({
                path: 'downloads',
                element: <BoardDownloadsPage />,
                handle: {
                    type: 'page',
                    label: t('downloads'),
                    icon: <CloudDownloadOutlinedIcon />,
                    iconSelected: <CloudDownloadIcon />,
                },
            });
        }
        if (permissions.includes('manage-users')) {
            routes.push({
                path: 'users',
                element: <UserManagementPage />,
                handle: {
                    type: 'page',
                    label: t('users'),
                    icon: <PeopleOutlinedIcon />,
                    iconSelected: <PeopleIcon />,
                },
            });
        }
        if (permissions.includes('change-settings')) {
            routes.push({
                path: 'settings',
                element: <EditableSettingsAdminPage />,
                handle: {
                    type: 'page',
                    label: t('settings'),
                    icon: <SettingsOutlinedIcon />,
                    iconSelected: <SettingsIcon />,
                },
            });
        }
    }

    return (
        <GlobalApp
            appTitle={t('appTitle')}
            basename={window.appPublicUrl}
            routes={routes}
            oktaUrl={appManifest.oktaUrl}
            profileAvatarSmall={<Avatar sx={{ width: 40, height: 40, bgcolor: themeColors.accent.green.bold }}>{initials}</Avatar>}
            profileAvatarLarge={<Avatar sx={{ width: 64, height: 64, bgcolor: themeColors.accent.green.bold }}>{initials}</Avatar>}
            profileName={profileName}
            profileOnClick={() =>
                showAlertDialog({
                    title: profileName,
                    content: (
                        <Typography component='div' variant='body2'>
                            <div>
                                {t('email')}: {email}
                            </div>
                            <div>{t('permissions')}:</div>
                            <ul style={{ margin: 0 }}>
                                {permissions.length > 0 ? (
                                    permissions.map((permission) => <li key={permission}>{t(`permissionEnum.${permission}`)}</li>)
                                ) : (
                                    <li>{t('noPermissions')}</li>
                                )}
                            </ul>
                        </Typography>
                    ),
                })
            }
            logOutOnClick={logOutOnClick}
            footerCopyright={t('copyright', { year: formatDateCustom(new Date(__BUILD_TIMESTAMP__), 'yyyy') })}
            footerLinks={[
                { label: t('privacyPolicy'), href: t('privacyPolicyLink') },
                { label: t('termsAndConditions'), href: t('termsAndConditionsLink') },
            ]}
        >
            <DialogHelper />
            <SnackbarHelper />
            <Outlet />
        </GlobalApp>
    );
};
